import axios from 'axios'
import { GetBaseUrl } from '@/utils/ConfigHelper'
import Response from '../../Common/Response'
import { PUBLIC_ERROR_CODE } from '../../../Error/Constants'
import { GetPublicErrorCode } from '../../../Error/Handler'

/**
 * 나의 작업 공간 파일 업로드
 * @param {string} token 
 * @param {string} videoId 
 * @param {File} source 
 */
const PostUpload = async (token, videoId, source) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/upload`
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const formData = new FormData()
        formData.append('videoId', videoId)
        formData.append('source', source)
        const response = await axios.post(url, formData, config)

        /*
            이미지 파일 업로드 시 Response Data
            {
                "originFileName": "저용량 이미지 1.jpg",
                "fileName": "a6858670-1493-11ed-bd7e-a3e89dc04b9a.jpg",
                "state": "Waiting", or "Encoding" or "Finish" or "Error"
                "originFileSize": 1100068,
                "fileSize": 1100068,
                "resolution": {
                    "width": 6000,
                    "height": 4000
                },
                "meta": {}
            }

            동영상 파일 업로드 시 Response Data
            {
                "originFileName": "저용량 동영상 2.mp4",
                "fileName": "4fd2c890149511eda2af95e768b8dc00.mp4",
                "state": "Waiting", or "Encoding" or "Finish" or "Error"
                "fileSize": 25440297,
                "resolution": {
                    "width": 1920,
                    "height": 1080
                },
                "meta": {
                    "duration": 36.161125
                }
            }
        */
        const data = response.data.data
        const result = (response.status === 200)
        return new Response(result, data)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_LIMITED_FILE:
            case PUBLIC_ERROR_CODE.ERR_LIMITED_FILE_SIZE:
            case PUBLIC_ERROR_CODE.ERR_NOT_SUPPORTED_FILE_FORMAT:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 인코딩 상태 조회
 * encodedStatus가 "NONE"이 아니면 인코딩 중...
 * @param {string} token 
 * @param {string} sourceId 
 */
const GetEncodedStatus = async (token, sourceId) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/encoded-status/${sourceId}`
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const response = await axios.get(url, config)

        /*
            Response Data
            {
                encodedStatus: "VIDEO" or "IMAGE" or "NONE"
            }
        */
        const data = response.data
        const result = (response.status === 200)

        return new Response(result, { ...data, _id: sourceId })
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.HTTP_STATUS_404:
            case PUBLIC_ERROR_CODE.HTTP_STATUS_500:
            default:
                // 이 녀석은 좀 특별하게 에러가 뜨면 무조건 false 반환
                return new Response(true, { code: publicErrorCode, _id: sourceId })
        }
    }
}

/**
 * 나의 소스 정보 조회
 * @param {string} token 
 * @param {string[]} sourceIds 
 */
const GetMySourceInfo = async (token, sourceIds) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/my-source-info`
        const config = { 
            params:  { ids: sourceIds },
            headers: { Authorization: `Bearer ${token}` } 
        }
        const response = await axios.get(url, config)

        /*
            Response Data 
           [
                // 이미지 소스 데이터 (imageSmallUrl, imageUrl)
                {
                    "_id": "62ebaf79bc476c0034c660c1",
                    "userId": "5f90f688edeb6d0034420bbe",
                    "videoId": "test-video-0000-1111-2222",
                    "originFileName": "20220522_205849.jpeg",
                    "fileName": "d24701d0-13e9-11ed-b15b-7fa953386ee9.jpeg",
                    "state": "Finish",
                    "fileType": "Image",
                    "format": ".jpeg",
                    "originFileSize": 716002,
                    "fileSize": 666528,
                    "resolution": {
                        "width": 4032,
                        "height": 3024
                    },
                    "meta": {},
                    "createdAt": "2022-08-04T11:37:29.884Z",
                    "imageSmallUrl": "https://videomonsterpublicdevnew.blob.core.windows.net/5f90f688edeb6d0034420bbe/d24701d0-13e9-11ed-b15b-7fa953386ee9.jpeg",
                    "imageUrl": "https://videomonsterpublicdevnew.blob.core.windows.net/5f90f688edeb6d0034420bbe/d24701d0-13e9-11ed-b15b-7fa953386ee9.jpeg",
                    "log": null
                },
                // 동영상 소스 데이터 (thumbnailUrl, videoUrl)
                {
                    "_id" : ObjectId("62ec73a0cdc0850041fb6679"),
                    "userId" : "5f90f688edeb6d0034420bbe",
                    "videoId" : "test-video-0000-1111-2222",
                    "originFileName" : "5eeea355389655.59822ff824b72 (1).avi",
                    "fileName" : "bcc7de90-145e-11ed-94cc-d3df634c67b7.avi",
                    "state" : "Finish",
                    "fileType" : "Video",
                    "format" : ".mp4",
                    "originFileSize" : 422740,
                    "fileSize" : 256768,
                    "resolution" : {
                        "width" : 800,
                        "height" : 600
                    },
                    "meta" : {
                        "duration" : 1.6
                    },
                    "createdAt" : ISODate("2022-08-05T01:34:24.886Z"),
                    "log" : null,
                    "thumbnailUrl" : "https://videomonsterpublicdevnew.blob.core.windows.net/5f90f688edeb6d0034420bbe/bcc7de90-145e-11ed-94cc-d3df634c67b7_thumb.jpg",
                    "videoUrl" : "https://videomonsterpublicdevnew.blob.core.windows.net/5f90f688edeb6d0034420bbe/bcc7de90-145e-11ed-94cc-d3df634c67b7.mp4"
                }
            ]
        */

        const result = (response.status === 200)
        const data = response.data.data
        return new Response(result, data)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * 유저가 비디오소스를 업로드하는 경우 로그 기록
 * @param {string} token 
 * @param {{
 *  videoId: string | null
 *  fileName: string | null
 *  fileSize: Number | null
 *  fileType: string | null
 *  duration: Number | null
 *  width: Number | null
 *  height: Number | null
 *  metadata: object | null
 * }} data 
 * @returns 
 */
 const PostSourceUploadLog = async (token, data = {}) => {
    try {
        const { videoId, fileName, fileSize , fileType, duration, width, height, metadata, description } = data
        if (metadata) {
            delete metadata.width
            delete metadata.height
            delete metadata.duration
        }

        const url = `${GetBaseUrl()}/apis/mysource/source-upload-log`
        const params = { videoId, width, height, description, createdAt: new Date() }
        const body = {
            fileName,
            fileSize,
            fileType,
            duration,
            metadata,
            params: JSON.stringify(params)
        }
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const response = await axios.post(url, body, config)

        const result = (response.status === 200)
        return new Response(result)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

const PostSourceUploadLogs = async (token, datas = []) => {
    try {
        if (!datas || datas.length === 0) return new Response(false)

        const logs = []
        datas.forEach(data => {
            const { videoId, fileName, fileSize , fileType, duration, width, height, metadata, description } = data
            if (metadata) {
                delete metadata.width
                delete metadata.height
                delete metadata.duration
            }
            const params = { videoId, width, height, description, createdAt: new Date() }
            const body = {
                fileName,
                fileSize,
                fileType,
                duration,
                metadata,
                params: JSON.stringify(params)
            }
            logs.push(body)
        })

        const url = `${GetBaseUrl()}/apis/mysource/source-upload-logs`
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const response = await axios.post(url, { logs }, config)

        const result = (response.status === 200)
        return new Response(result)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token
 * @param {File} source
 * @returns {Promise<Response<{
 *  _id: string,
 *  fileName: string,
 *  fileSize: number,
 *  meta: object | null,
 *  resolution: { width: number, height: number },
 *  state: string,
 * }>>}
 */
const PostMySourceUpload = async (token, source) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/upload`
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const formData = new FormData()
        formData.append('source', source)
        const response = await axios.post(url, formData, config)

        const data = response.data.data
        const result = (response.status === 200)
        return new Response(result, data)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
			case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
			case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token
 * @param {{
 *  source: File,
 *  provider: string,
 *  sourceType: string,
 *  isTempSave?: boolean,
 * }} data
 * @returns {Promise<Response<{
*  _id: string,
* }>>}
*/
const PostMySourceUploadThumb = async (token, data) => {
   try {
       const { source, provider, sourceType, isTempSave } = data
       const url = `${GetBaseUrl()}/apis/mysource/upload-thumb`
       const config = { headers: { Authorization: `Bearer ${token}` } }
       const formData = new FormData()
       formData.append('source', source)
       formData.append('provider', provider)
       formData.append('sourceType', sourceType)
       if (isTempSave !== undefined && isTempSave !== null) formData.append('isTempSave', isTempSave)
       const response = await axios.post(url, formData, config)

       const resultData = response.data.data
       const result = (response.status === 200)
       return new Response(result, resultData)
   }
   catch (e) {
       const publicErrorCode = GetPublicErrorCode(e)
       switch (publicErrorCode) {
           case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
           case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
               return new Response(false, publicErrorCode)
           case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
           default:
               throw e
       }
   }
}

/**
 * @param {string} token
 * @param {{
 *  id: string,
 *  source: File,
 *  provider: string,
 * }} data
 * @returns {Promise<Response<{
*  _id: string,
*  fileName: string,
*  fileSize: number,
*  meta: object | null,
*  resolution: { width: number, height: number },
*  state: string,
* }>>}
*/
const PostMySourceUploadFile = async (token, data) => {
   try {
       const { id, source, provider } = data
       const url = `${GetBaseUrl()}/apis/mysource/upload-file`
       const config = { headers: { Authorization: `Bearer ${token}` } }
       const formData = new FormData()
       formData.append('sourceId', id)
       formData.append('source', source)
       formData.append('provider', provider)
       const response = await axios.post(url, formData, config)

       const resultData = response.data.data
       const result = (response.status === 200)
       return new Response(result, resultData)
   }
   catch (e) {
       const publicErrorCode = GetPublicErrorCode(e)
       switch (publicErrorCode) {
           case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
           case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
               return new Response(false, publicErrorCode)
           case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
           default:
               throw e
       }
   }
}

/**
 * @param {string} token
 * @param {Array<{
 *  productName: string,
 *  texts: Array<string>,
 * }>} datas
 * @returns {Promise<Response<Array<{
 *  userId: string,
 *  fileName: string,
 *  state: string,
 *  format: string,
 *  provider: string,
 *  originFileSize: string,
 *  fileSize: string,
 *  resolution: null,
 *  meta: null,
 *  texts: Array<string>,
 *  log: null | string,
 *  createdAt: string,
 *  _id: string,
 * }>>>}
 */
const PostMySourceRegistTexts = async (token, datas) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/regist-texts`;
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const body = { datas };
        const response = await axios.post(url, body, config);

        const data = response.data.data
        const result = (response.status === 200)
        return new Response(result, data)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
			case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
			case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token
 * @param {{
 *  provider: string,
 *  providerData: Object,
 *  url: string,
 *  isVideo?: boolean,
 * }[]} datas
 * @returns {Promise<Object>}
 */
const PostMySourceRegistStocks = async (token, datas) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/regist-stocks`;
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const body = { datas };
        const response = await axios.post(url, body, config);

        const data = response.data.data
        const result = (response.status === 200)
        return new Response(result, data)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
			case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
			case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token
 * @param {{ id: string, texts: Array<string> }} data
 */
const PatchMySourceText = async (token, data) => {
    try {
        const { id, texts } = data
        const url = `${GetBaseUrl()}/apis/mysource/${id}/text`
        const config = { headers: { Authorization: `Bearer ${token}` } }
        const body = { texts }
        const response = await axios.patch(url, body, config)

        const result = (response.status === 200)
        return new Response(result)
    }
    catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token 
 * @param {{
 *      sort: number,
 *      filter: number,
 *      offset: number,
 *      limit: number
 *  }} data
 * @returns {Promise<Response<{
 * list: Array<{
 *   _id: string,
 *   userId: string,
 *   originFileName: string,
 *   originFileSize: number,
 *   fileName: string,
 *   fileSize: number,
 *   state: string,
 *   type: "Image" | "Video" | "Text" | "StockImage",
 *   format: string | null,
 *   provider: "User" | "Pixabay" | "Gettyimages" | null,
 *   thumbnailUrl: string,
 *   url: string,
 *   fileName: string,
 *   texts: Array<string> | null,
 *   providerData: {
 *      url: string,
 *      width: number,
 *      height: number,
 *   } | null,
 *   resolution: {
 *      width: number,
 *      height: number
 *   },
 *   meta: object | null,
 *   log: object | null,
 *   createdAt: string,
 * }>,
 * count: number,
 * }>>}
 */
const GetMySource = async (token, data = {}) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource`;
		const params = {
            sort: data.sort,
            filter: data.filter,
            offset: data.offset,
            limit: data.limit
        }
        const config = { params, headers: { Authorization: `Bearer ${token}` } }
		const response = await axios.get(url, config)
        const result = (response.status === 200);
        const resultData = response.data.data;

        return new Response(result, resultData)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

/**
 * @param {string} token 
 * @returns {Promise<Response>}
 */
const GetMySourceCapacity = async (token) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource/capacity`;
        const config = { headers: { Authorization: `Bearer ${token}` } }
		const response = await axios.get(url, config)
        const result = (response.status === 200);
        const resultData = response.data.data;

        return new Response(result, resultData)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                return new Response(false, PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR)
        }
    }
}

/**
 * @param {string} token 
 * @param {Array<string>} ids
 * @returns {Promise<Response>}
 */
const DeleteMySource = async (token, ids) => {
    try {
        const url = `${GetBaseUrl()}/apis/mysource`;
		const config = { params: { ids: ids.join(",") }, headers: { Authorization: `Bearer ${token}` } }
		const response = await axios.delete(url, config)
        const result = (response.status === 200);
        const resultData = response.data.data;

        return new Response(result, resultData)
    } catch (e) {
        const publicErrorCode = GetPublicErrorCode(e)
        switch (publicErrorCode) {
            case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
            case PUBLIC_ERROR_CODE.ERR_NOT_FOUND:
                return new Response(false, publicErrorCode)
            case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
            default:
                throw e
        }
    }
}

export default {
    PostUpload,
    GetEncodedStatus,
    GetMySourceInfo,
    PostSourceUploadLog,
    PostSourceUploadLogs,

	PostMySourceUpload,
    PostMySourceUploadThumb,
    PostMySourceUploadFile,
	PostMySourceRegistTexts,
	PostMySourceRegistStocks,
    PatchMySourceText,
	GetMySource,
	GetMySourceCapacity,
	DeleteMySource
}